import React, { FC } from 'react';
import { GridCol, GridRow } from '@naf/grid';
import { Text, TextVariant } from '@naf/text';
import { Close } from '@styled-icons/material/Close';
import { StepWizardChildProps } from 'react-step-wizard';
import { Controller, FormProvider } from 'react-hook-form';
import { WizardNavigation } from '@pages/BecomeAMember/components/WizardNavigation';
import { Spinner } from '@components/spinner/Spinner';
import { ProductSelect } from './fieldInputs/Product';
import { Pickup } from './fieldInputs/Pickup/Pickup';
import { SummaryProvider } from '../../components/Summary/Summary';
import * as S from './DrivingPermit.styles';
import { defaultProduct, useDrivingPermitLogic } from './useDrivingPermitLogic';

export { defaultProduct };

export const DrivingPermit: FC<Partial<StepWizardChildProps>> = ({
  totalSteps,
  currentStep,
  nextStep,
  previousStep,
}) => {
  const {
    form,
    errors,
    products,
    isUpdating,
    isMounted,
    hasSelected,
    pickupAtOffice,
    internationalDrivingPermitForm,
    options,
    selectedCountryObjects,
    addCountry,
    removeCountry,
    onSubmit,
  } = useDrivingPermitLogic(currentStep);
  const { control, handleSubmit } = form;

  if (!products && isUpdating) return <Spinner />;

  return products ? (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...form}>
      <GridRow>
        <GridCol s="12" m="12" l="8" xl="8">
          <form onSubmit={handleSubmit(onSubmit)}>
            <S.Ingress>
              <S.NoMarginText variant={TextVariant.Header3}>Velg type førerkort</S.NoMarginText>
              <S.NoMarginText>
                For å kunne gjennomføre bestilling av internasjonalt førerkort må du har førerkort registrert i
                Norge/EU/EØS, og ha folkeregistrert adresse i Norge.
              </S.NoMarginText>
            </S.Ingress>
            <S.StyledSelect
              label="Legg til land"
              options={options}
              placeholder="Velg land"
              handleSelect={(option: Record<string, string>) => addCountry(option.label)}
              maxLines={6}
            />
            {selectedCountryObjects.length > 0 && (
              <S.CountryAdditionalInfo>
                {selectedCountryObjects.map((country) => (
                  <S.CountryWrapper key={country.countryName}>
                    <S.TextWrapper>
                      <Text variant={TextVariant.ArticleTextHeader}>{country.countryName}</Text>
                      <Text>{country.additionalInfo}</Text>
                    </S.TextWrapper>
                    <S.CloseButton onClick={() => removeCountry(country.countryName)}>
                      <Close size={18} />
                    </S.CloseButton>
                  </S.CountryWrapper>
                ))}
              </S.CountryAdditionalInfo>
            )}
            <S.InlineLabel htmlFor="productField">
              <Controller
                name="product"
                control={control}
                defaultValue={
                  (internationalDrivingPermitForm && internationalDrivingPermitForm.product) || defaultProduct
                }
                render={ProductSelect}
              />
            </S.InlineLabel>
            {hasSelected && !pickupAtOffice && (
              <S.PostalInfoText>
                Fullfører du bestillingen, vil du motta førerkort i posten. Behandlingstid ca en uke.
              </S.PostalInfoText>
            )}
            <S.StyledBlock>
              <Text variant={TextVariant.Header3}>Kort tid til avreise?</Text>
              <Pickup control={control} defaultValue={internationalDrivingPermitForm?.pickupAtOffice || false} />
            </S.StyledBlock>
          </form>
        </GridCol>
        {isMounted && (
          <GridCol s="12" m="12" l="4" xl="4" justify={{ s: 'stretch', m: 'stretch', l: 'end', xl: 'end' }}>
            <SummaryProvider control={control} useOriginalPrice />
          </GridCol>
        )}
        <GridCol s="12" m="12" l="12" xl="12">
          <WizardNavigation
            totalSteps={totalSteps}
            currentStep={currentStep}
            nextStep={nextStep}
            previousStep={previousStep}
            isValid={Object.values(errors).length === 0 && hasSelected}
            onSubmit={onSubmit}
          />
        </GridCol>
      </GridRow>
    </FormProvider>
  ) : (
    <Spinner />
  );
};
